// PlacementWizard.jsx — bottom-center HUD showing placement instructions (ticket 03 extraction).

import React from 'react';
import Box from '@mui/material/Box';
import { getDrawRuleMeta } from '../drawRuleMeta';
import { MAP_OVERLAY_Z } from './mapEditorConstants';

// rerender-memo: parent (MapEditorModalInner) re-renders on every vertex placed (event-driven
// placedCount updates) — memoized like ContextPanel/MapDrawToolbar so this HUD only actually
// re-renders when one of its own props changes.
const PlacementWizard = React.memo(function PlacementWizard({ tacticalSidc, pointRange, drawMode, drawRule, placedCount }) {
    if (!tacticalSidc || !drawMode) return null;
    const { min, max } = pointRange || { min: 2, max: Number.MAX_VALUE };
    const isFixed = max < Number.MAX_VALUE && max === min && min > 1;
    const isUnlimited = max >= Number.MAX_VALUE;

    // Per-point step hint from metadata (highest priority for variable-count rules)
    const drMeta = drawRule != null ? getDrawRuleMeta(drawRule) : null;
    const placed = placedCount ?? 0;
    const nextLabel = !isFixed ? (drMeta?.pointLabels?.[placed] ?? null) : null;

    let text;
    if (isFixed) {
        text = `Click to place — drag vertices to adjust.`;
    } else if (nextLabel) {
        const total = drMeta.pointLabels.length;
        text = `${nextLabel} (${placed + 1} of ${total})`;
    } else if (drawMode === 'draw-point') {
        text = 'Click on the map to place.';
    } else if (drawMode === 'draw-circle') {
        text = 'Click to place center — click again to set radius.';
    } else if (isUnlimited) {
        text = `Minimum ${min} point${min !== 1 ? 's' : ''} — double-click or Enter to finish.`;
    } else {
        text = `${min}–${max} points — double-click or Enter to finish.`;
    }

    return (
        <Box sx={(theme) => ({
            position: 'absolute', bottom: 64, left: '50%', transform: 'translateX(-50%)',
            zIndex: MAP_OVERLAY_Z.PLACEMENT_WIZARD, pointerEvents: 'none',
            bgcolor: theme.palette.primary.main, color: '#fff',
            px: 1.5, py: 0.5, borderRadius: 2,
            fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
            boxShadow: theme.shadows[4],
            whiteSpace: 'nowrap',
        })}>
            {text}
        </Box>
    );
});

export default PlacementWizard;
